import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as MultipleSelect } from 'assets/icons/multipleSelect.svg';
import { ReactComponent as Paragraph } from 'assets/icons/paragraphTxt.svg';
import { ReactComponent as Prioritise } from 'assets/icons/prioritise.svg';
import { ReactComponent as ShortAnswer } from 'assets/icons/shortTxt.svg';
import { ReactComponent as SingleSelect } from 'assets/icons/singleSelect.svg';
import Checkbox from 'components/Checkbox/Checkbox';
import TextField from 'components/TextField/TextField';
import { SURVEY_QUESTION_TYPES } from 'constants/materialAssessment';

import Select from '../../../../../../components/Select/Select';
import CreateSurveyItemPanel from './CreateSurveyItemPanel';
import CreateSurveyQuestionOptions from './CreateSurveyQuestionOptions';

import type IAnswerTypeOption from '../interfaces/IAnswerTypeOption';
import type { ICreateSurveyQuestion } from '../interfaces/ICreateSurveyQuestion';

import styles from '../scss/CreateSurveyQuestion.module.scss';

function CreateSurveyQuestion({ control, index, onRemove }: ICreateSurveyQuestion) {
  const { t } = useTranslation();

  const questionType = useWatch({
    control,
    name: `questions.${index}.questionType`,
  });

  const {
    fields,
    append,
    remove,
    move,
  } = useFieldArray({
    control,
    name: `questions.${index}.options`,
  });

  const showOptions = useMemo(() => questionType === SURVEY_QUESTION_TYPES.SINGLE_SELECT
    || questionType === SURVEY_QUESTION_TYPES.MULTIPLE_SELECT
    || questionType === SURVEY_QUESTION_TYPES.PRIORITIZE, [questionType]);

  useEffect(() => {
    if (showOptions && fields.length === 0) {
      append({
        name: '',
      });
    }
  }, [showOptions, fields.length, append]);

  const answerTypeOptions = useMemo((): IAnswerTypeOption[] => [
    {
      value: SURVEY_QUESTION_TYPES.SHORT_ANSWER,
      label: t('materialityAssessment.answerTypes.short'),
      icon: <ShortAnswer />,
    },
    {
      value: SURVEY_QUESTION_TYPES.PARAGRAPH,
      label: t('materialityAssessment.answerTypes.paragraph'),
      icon: <Paragraph />,
    },
    {
      value: SURVEY_QUESTION_TYPES.SINGLE_SELECT,
      label: t('materialityAssessment.answerTypes.singleSelect'),
      icon: <SingleSelect />,
    },
    {
      value: SURVEY_QUESTION_TYPES.MULTIPLE_SELECT,
      label: t('materialityAssessment.answerTypes.multipleSelect'),
      icon: <MultipleSelect />,
    },
    {
      value: SURVEY_QUESTION_TYPES.PRIORITIZE,
      label: t('materialityAssessment.answerTypes.prioritise'),
      icon: <Prioritise />,
    },
  ], [t]);

  const handleAddOption = () => {
    append({
      name: '',
    });
  };

  const handleRemoveOption = (optionIndex: number) => {
    if (fields.length > 1) {
      remove(optionIndex);
    }
  };

  const handleMoveOption = useCallback((dragIndex: number, hoverIndex: number) => {
    move(dragIndex, hoverIndex);
  }, [move]);

  return (
    <CreateSurveyItemPanel
      title={`${t('materialityAssessment.fields.question')} ${index + 1}`}
      onDelete={onRemove}
    >
      <div className={styles.questionWrapper}>
        <Controller
          control={control}
          name={`questions.${index}.name`}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <TextField
              name={name}
              value={value}
              label={t('materialityAssessment.questionText')}
              error={error?.message}
              onChange={(event) => onChange(event.target.value)}
              required
              placeholder={t('materialityAssessment.enterQuestionText')}
            />
          )}
        />
        <Controller
          control={control}
          name={`questions.${index}.questionType`}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <Select
              name={name}
              value={answerTypeOptions.find((option) => option.value === value)}
              label={t('materialityAssessment.answerType')}
              error={error?.message}
              onChange={(selectedOption: unknown) => {
                const option = selectedOption as IAnswerTypeOption | null;
                onChange(option?.value);
              }}
              required
              options={answerTypeOptions}
              formatOptionLabel={(option: IAnswerTypeOption) => (
                <div className={styles.answerTypeOption}>
                  {option.icon}
                  <span>{option.label}</span>
                </div>
              )}
            />
          )}
        />
      </div>

      {showOptions && questionType ? (
        <CreateSurveyQuestionOptions
          control={control}
          questionIndex={index}
          fields={fields}
          answerType={questionType}
          onAddOption={handleAddOption}
          onRemoveOption={handleRemoveOption}
          onMoveOption={handleMoveOption}
        />
      ) : null}
      <Controller
        control={control}
        name={`questions.${index}.isCommentable`}
        render={({ field: { name, value, onChange } }) => (
          <Checkbox
            name={name}
            value="isCommentable"
            checked={!!value}
            className={styles.justificationCheckbox}
            label={t('materialityAssessment.addJustificationField')}
            onChange={(event) => onChange(event.target.checked)}
          />
        )}
      />
    </CreateSurveyItemPanel>
  );
}

export default CreateSurveyQuestion;
